import React, { useState } from 'react';
import { OpenAIClient } from '../../utils/openaiClient';

const ArtemisSDLC = () => {
  const [selectedPhase, setSelectedPhase] = useState('development');
  const [selectedView, setSelectedView] = useState('technical'); // 'technical' or 'stories'
  const [showDetails, setShowDetails] = useState({});
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [summaryProgress, setSummaryProgress] = useState('');

  const toggleDetails = (id) => {
    setShowDetails(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Function to generate markdown summary
  const generateMarkdownSummary = async () => {
    setIsGeneratingSummary(true);
    setSummaryProgress('Initializing AI client...');
    
    try {
      const openai = new OpenAIClient();
      
      setSummaryProgress('Generating comprehensive SDLC integration guide...');
      
      const summaryPrompt = `Generate a comprehensive markdown guide for Artemis SDLC Integration. Include:

1. Executive Summary of Artemis in SDLC
2. Detailed breakdown of each integration point:
   - Code Audit & Quality Analysis
   - IDE Integration (VS Code, Cursor, etc.)
   - CI/CD Pipeline Integration
   - MCP Agent Tools
3. Implementation guides for each integration
4. Best practices and workflows
5. ROI and benefits analysis
6. Mermaid diagrams showing the complete SDLC flow
7. Code examples for integrations

Make it detailed, technical, and suitable for enterprise documentation.`;

      const summary = await openai.getChatCompletion(summaryPrompt, {
        systemPrompt: `You are a technical documentation expert specializing in DevOps and SDLC processes. Create detailed markdown documentation with Mermaid diagrams, code examples, and implementation guides. Focus on practical, actionable content for developers and DevOps engineers.`,
        temperature: 0.7,
        maxTokens: 4000
      });

      // Create the full markdown document
      const fullMarkdown = `# Artemis SDLC Integration Guide

Generated on: ${new Date().toLocaleString()}

---

${summary}

---

## Appendix: Quick Start Commands

### IDE Integration
\`\`\`bash
# VS Code / Cursor Extension
ext install artemis-ai.artemis-code-intelligence

# Configure API
artemis config set api-key YOUR_API_KEY
\`\`\`

### CI/CD Integration
\`\`\`yaml
# GitHub Actions Example
- name: Artemis Code Review
  uses: artemis-ai/code-review-action@v1
  with:
    api-key: \${{ secrets.ARTEMIS_API_KEY }}
    threshold: 7.5
\`\`\`

### MCP Agent Setup
\`\`\`json
{
  "mcpServers": {
    "artemis": {
      "command": "artemis-mcp",
      "args": ["--mode", "agent"],
      "env": {
        "ARTEMIS_API_KEY": "your-key"
      }
    }
  }
}
\`\`\`

---

*This document was generated using Artemis AI-powered documentation tools.*`;

      // Download the markdown file
      setSummaryProgress('Preparing download...');
      const blob = new Blob([fullMarkdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `artemis-sdlc-integration-guide-${Date.now()}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setSummaryProgress('Guide downloaded successfully!');
      setTimeout(() => {
        setIsGeneratingSummary(false);
        setSummaryProgress('');
      }, 2000);
      
    } catch (error) {
      console.error('Error generating summary:', error);
      setSummaryProgress('Error: ' + error.message);
      setTimeout(() => {
        setIsGeneratingSummary(false);
        setSummaryProgress('');
      }, 3000);
    }
  };

  const generateExecutiveSummary = async () => {
    const summary = `# Artemis: AI-Powered Code Intelligence for Enterprise

## Executive Summary

Artemis seamlessly integrates with your existing development tools to provide AI-powered code optimization, security enhancement, and technical debt reduction across your entire software development lifecycle.

## Integration Capabilities

### 🔧 Works With Your Existing Tools
- **Static Analysis**: Enhances SonarQube, Checkmarx, Fortify
- **Security Scanning**: Complements Snyk, Veracode, WhiteSource  
- **Performance Tools**: Integrates with VTune, JProfiler, DataDog
- **IDEs**: VS Code, IntelliJ IDEA, Eclipse, Cursor
- **CI/CD**: Jenkins, GitHub Actions, GitLab CI, Azure DevOps
- **Version Control**: GitHub, GitLab, Bitbucket

### 🤝 How Artemis Enhances Your Tools
1. **Uploads tool reports** → AI analyzes all issues holistically
2. **Generates actual fixes** → Not just identifies problems
3. **Handles conflicts** → Resolves when fixing one issue breaks another
4. **Provides context** → Understands business logic, not just code rules

## Key Use Cases for Banking & Financial Services

### 1. 🔒 Security & Compliance
- **Scenario**: Snyk identifies 15 vulnerabilities in payment processing
- **Artemis Solution**: Generates secure fixes that maintain PCI compliance
- **Result**: All vulnerabilities patched without breaking functionality

### 2. 🚀 Legacy Modernization
- **Scenario**: 10-year-old Java system needs cloud migration
- **Artemis Solution**: Gradual, safe refactoring with automated testing
- **Result**: Modern microservices with zero downtime

### 3. 💰 Cost Optimization
- **Scenario**: High cloud costs from inefficient code
- **Artemis Solution**: Multi-objective optimization for performance & cost
- **Result**: 50% cost reduction while improving response times

### 4. 👥 Developer Productivity
- **Scenario**: Developers spending 40% time on code reviews
- **Artemis Solution**: Real-time IDE feedback prevents issues
- **Result**: First-time PR approval rate increases by 75%

## Simple Integration Process

### Week 1: Pilot Setup
\`\`\`
1. Install Artemis plugins in developer IDEs
2. Connect to your Git repositories  
3. Configure CI/CD integration
\`\`\`

### Week 2-4: Team Adoption
\`\`\`
1. Developers see real-time code scores
2. Automated PR reviews provide feedback
3. Upload existing tool reports for AI fixes
\`\`\`

### Month 2+: Full Value Realization
\`\`\`
1. 60% reduction in production bugs
2. 40% faster feature delivery
3. Measurable technical debt reduction
\`\`\`

## Why Artemis for Financial Institutions?

### ✅ Regulatory Compliance
- Ensures code meets security standards
- Provides audit trails for all changes
- Maintains compliance during optimization

### ✅ Risk Reduction
- AI understands financial logic patterns
- Prevents breaking changes in critical systems
- Extensive testing of all modifications

### ✅ ROI & Efficiency
- Reduces manual code review time by 70%
- Cuts debugging time in half
- Accelerates feature delivery by 40%

## The Artemis Difference

**Traditional Tools**: "Your code has 47 issues"
**Artemis**: "Here are the fixes, tested and ready to deploy"

While tools like SonarQube identify problems, Artemis understands your code's intent and generates intelligent solutions that actually improve your codebase.

## Next Steps

1. **30-min Demo**: See Artemis in action with your code
2. **Pilot Program**: 4-week trial with your team
3. **Full Deployment**: Enterprise-wide rollout

## Contact

**Enterprise Sales**: enterprise@turintech.ai
**Technical Support**: support@turintech.ai
**Website**: https://artemis.turintech.ai

---

*Artemis: Where AI meets Code Intelligence*`;

    const blob = new Blob([summary], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'artemis-executive-summary.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Visual components
  const SDLCPhase = ({ phase, icon, title, description, isActive, onClick }) => (
    <div
      onClick={onClick}
      style={{
        background: isActive 
          ? 'linear-gradient(135deg, rgba(78, 205, 196, 0.2), rgba(78, 205, 196, 0.1))' 
          : 'rgba(255, 255, 255, 0.05)',
        padding: '20px',
        borderRadius: '12px',
        border: `2px solid ${isActive ? '#4ECDC4' : 'rgba(255, 255, 255, 0.1)'}`,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        transform: isActive ? 'scale(1.02)' : 'scale(1)',
        textAlign: 'center'
      }}
    >
      <div style={{ fontSize: '36px', marginBottom: '10px' }}>{icon}</div>
      <h4 style={{ 
        margin: '0 0 5px 0', 
        fontSize: '16px',
        color: isActive ? '#4ECDC4' : 'white'
      }}>
        {title}
      </h4>
      <p style={{ 
        margin: 0, 
        fontSize: '12px', 
        opacity: 0.8,
        lineHeight: '1.4'
      }}>
        {description}
      </p>
    </div>
  );

  const FeatureCard = ({ icon, title, description, features, color = '#4ECDC4' }) => (
    <div style={{
      background: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '12px',
      padding: '25px',
      border: `1px solid ${color}40`,
      height: '100%'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '15px',
        gap: '15px'
      }}>
        <div style={{
          fontSize: '32px',
          width: '50px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `${color}20`,
          borderRadius: '10px'
        }}>
          {icon}
        </div>
        <h3 style={{ margin: 0, fontSize: '20px', color }}>{title}</h3>
      </div>
      <p style={{ 
        margin: '0 0 20px 0', 
        fontSize: '14px', 
        opacity: 0.9,
        lineHeight: '1.5'
      }}>
        {description}
      </p>
      <ul style={{ 
        margin: 0, 
        paddingLeft: '20px',
        fontSize: '13px',
        lineHeight: '1.8'
      }}>
        {features.map((feature, i) => (
          <li key={i} style={{ marginBottom: '8px' }}>{feature}</li>
        ))}
      </ul>
    </div>
  );

  const IntegrationFlow = ({ title, steps, color }) => (
    <div style={{
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '20px'
    }}>
      <h4 style={{ margin: '0 0 20px 0', color, fontSize: '18px' }}>{title}</h4>
      <div style={{ position: 'relative' }}>
        {steps.map((step, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: i < steps.length - 1 ? '20px' : '0'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '600',
              fontSize: '16px',
              flexShrink: 0
            }}>
              {i + 1}
            </div>
            <div style={{
              marginLeft: '20px',
              flex: 1,
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '12px 16px',
              borderRadius: '8px',
              fontSize: '14px'
            }}>
              <strong>{step.action}:</strong> {step.description}
            </div>
            {i < steps.length - 1 && (
              <div style={{
                position: 'absolute',
                left: '20px',
                top: `${40 + i * 60}px`,
                width: '2px',
                height: '20px',
                background: `${color}40`
              }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const CodeExample = ({ title, code, language = 'javascript', color = '#4ECDC4' }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    return (
      <div style={{
        background: 'rgba(0, 0, 0, 0.4)',
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${color}40`,
        marginBottom: '20px'
      }}>
        <div 
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            background: `${color}20`,
            padding: '10px 15px',
            borderBottom: `1px solid ${color}40`,
            fontSize: '13px',
            fontWeight: '600',
            color: color,
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            userSelect: 'none'
          }}
        >
          <span>{title}</span>
          <span style={{
            fontSize: '16px',
            transition: 'transform 0.3s ease',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </span>
        </div>
        {isExpanded && (
          <pre style={{
            margin: 0,
            padding: '15px',
            fontSize: '12px',
            lineHeight: '1.5',
            color: '#ddd',
            overflow: 'auto',
            maxHeight: '400px',
            animation: 'slideDown 0.3s ease'
          }}>
            <code>{code}</code>
          </pre>
        )}
      </div>
    );
  };

  const MetricCard = ({ label, value, trend, color }) => (
    <div style={{
      background: 'rgba(0, 0, 0, 0.3)',
      padding: '20px',
      borderRadius: '8px',
      textAlign: 'center',
      border: `1px solid ${color}40`
    }}>
      <div style={{ fontSize: '32px', fontWeight: '700', color, marginBottom: '5px' }}>
        {value}
      </div>
      <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '5px' }}>
        {label}
      </div>
      {trend && (
        <div style={{ fontSize: '12px', color: trend > 0 ? '#4ECDC4' : '#FF6B6B' }}>
          {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
      color: 'white',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <h1 style={{ 
          margin: '0 0 15px 0', 
          fontSize: '42px',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #4ECDC4, #44A08D)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          🔄 Artemis in the SDLC Process
        </h1>
        <p style={{ margin: '0 0 40px 0', fontSize: '20px', opacity: 0.8 }}>
          Comprehensive AI-powered code intelligence throughout your development lifecycle
        </p>

        {/* Summary Generation Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '15px',
          marginBottom: '20px'
        }}>
          <button
            onClick={generateExecutiveSummary}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #DDA0DD, #9370DB)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            💼 Executive Summary
          </button>
          <button
            onClick={generateMarkdownSummary}
            disabled={isGeneratingSummary}
            style={{
              padding: '12px 24px',
              background: isGeneratingSummary ? '#666' : 'linear-gradient(135deg, #4ECDC4, #44A08D)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isGeneratingSummary ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            {isGeneratingSummary ? (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid white',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                {summaryProgress || 'Generating...'}
              </>
            ) : (
              <>
                📄 Generate Integration Guide
              </>
            )}
          </button>
        </div>

        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            @keyframes slideDown {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}
        </style>

        {/* View Toggle */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '30px',
          justifyContent: 'center'
        }}>
          <button
            onClick={() => setSelectedView('technical')}
            style={{
              padding: '12px 30px',
              background: selectedView === 'technical' 
                ? 'linear-gradient(135deg, #4ECDC4, #44A08D)' 
                : 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: `2px solid ${selectedView === 'technical' ? '#4ECDC4' : 'rgba(255, 255, 255, 0.2)'}`,
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            🔧 Technical View
          </button>
          <button
            onClick={() => setSelectedView('stories')}
            style={{
              padding: '12px 30px',
              background: selectedView === 'stories' 
                ? 'linear-gradient(135deg, #DDA0DD, #9370DB)' 
                : 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: `2px solid ${selectedView === 'stories' ? '#DDA0DD' : 'rgba(255, 255, 255, 0.2)'}`,
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            👥 User Stories
          </button>
        </div>

        {/* Technical View */}
        {selectedView === 'technical' && (
          <>
            {/* SDLC Phases */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '30px',
              borderRadius: '16px',
              marginBottom: '40px'
            }}>
              <h2 style={{ margin: '0 0 25px 0', fontSize: '24px' }}>
                Select SDLC Phase
              </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px'
          }}>
            <SDLCPhase
              phase="development"
              icon="💻"
              title="Development"
              description="IDE integration, real-time code analysis"
              isActive={selectedPhase === 'development'}
              onClick={() => setSelectedPhase('development')}
            />
            <SDLCPhase
              phase="review"
              icon="🔍"
              title="Code Review"
              description="AI-powered code audits and quality checks"
              isActive={selectedPhase === 'review'}
              onClick={() => setSelectedPhase('review')}
            />
            <SDLCPhase
              phase="cicd"
              icon="🚀"
              title="CI/CD"
              description="Automated PR reviews and optimization"
              isActive={selectedPhase === 'cicd'}
              onClick={() => setSelectedPhase('cicd')}
            />
            <SDLCPhase
              phase="monitoring"
              icon="📊"
              title="Monitoring"
              description="Continuous code quality tracking"
              isActive={selectedPhase === 'monitoring'}
              onClick={() => setSelectedPhase('monitoring')}
            />
          </div>
        </div>

        {/* Development Phase */}
        {selectedPhase === 'development' && (
          <div>
            {/* IDE Integration */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h2 style={{ margin: '0 0 30px 0', color: '#4ECDC4' }}>
                🔧 IDE Integration
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '25px',
                marginBottom: '30px'
              }}>
                <FeatureCard
                  icon="🎯"
                  title="VS Code & Cursor"
                  description="Native extensions for seamless integration"
                  features={[
                    'Real-time code scoring as you type',
                    'Inline optimization suggestions',
                    'One-click variation generation',
                    'Context-aware improvements',
                    'Performance impact preview'
                  ]}
                  color="#4ECDC4"
                />
                
                <FeatureCard
                  icon="🤖"
                  title="MCP Agent Tools"
                  description="Access Artemis agents through Model Context Protocol"
                  features={[
                    'Coding agent for complex refactoring',
                    'Performance optimization agent',
                    'Security analysis agent',
                    'Documentation generation agent',
                    'Test generation agent'
                  ]}
                  color="#96CEB4"
                />
                
                <FeatureCard
                  icon="⚡"
                  title="Local Analysis"
                  description="Score and optimize code without leaving your IDE"
                  features={[
                    'Instant code quality metrics',
                    'Complexity analysis',
                    'Performance bottleneck detection',
                    'Memory usage optimization',
                    'Best practice enforcement'
                  ]}
                  color="#DDA0DD"
                />
              </div>

              <IntegrationFlow
                title="How IDE Integration Works"
                color="#4ECDC4"
                steps={[
                  {
                    action: "Install Extension",
                    description: "Add Artemis to VS Code/Cursor from marketplace"
                  },
                  {
                    action: "Configure API",
                    description: "Connect to Artemis cloud or on-premise instance"
                  },
                  {
                    action: "Write Code",
                    description: "Get real-time feedback and suggestions"
                  },
                  {
                    action: "Generate Variations",
                    description: "See AI-powered improvements with one click"
                  },
                  {
                    action: "Apply Changes",
                    description: "Accept optimizations with confidence scores"
                  }
                ]}
              />

              <CodeExample
                title="VS Code Extension Configuration"
                color="#4ECDC4"
                code={`// .vscode/settings.json
{
  "artemis.enabled": true,
  "artemis.apiEndpoint": "https://api.artemis.ai",
  "artemis.apiKey": "\${ARTEMIS_API_KEY}",
  
  // Real-time analysis settings
  "artemis.realTimeAnalysis": {
    "enabled": true,
    "debounceMs": 1000,
    "minFileSize": 100,
    "maxFileSize": 10000
  },
  
  // Optimization preferences
  "artemis.optimization": {
    "autoSuggest": true,
    "performanceThreshold": 7.5,
    "showInlineHints": true,
    "enableVariationGeneration": true
  },
  
  // MCP Agent configuration
  "artemis.mcpAgents": {
    "enabled": true,
    "availableAgents": [
      "coding",
      "performance",
      "security",
      "documentation",
      "testing"
    ]
  }
}`}
              />

              <CodeExample
                title="Using Artemis MCP Agents in Cursor"
                color="#96CEB4"
                code={`// Example: Using Artemis Coding Agent through MCP

// 1. Configure MCP in Cursor settings
{
  "mcpServers": {
    "artemis": {
      "command": "npx",
      "args": ["@artemis/mcp-server"],
      "env": {
        "ARTEMIS_API_KEY": "your-api-key"
      }
    }
  }
}

// 2. Use in your code with @artemis mentions
// @artemis optimize this function for performance
function processLargeDataset(data) {
  // Current implementation
  const results = [];
  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    if (item.value > 100) {
      results.push(transformItem(item));
    }
  }
  return results;
}

// 3. Artemis agent responds with optimized version:
// "I'll optimize this for better performance using array methods and parallel processing"

function processLargeDataset(data) {
  // Optimized by Artemis Performance Agent
  return data
    .filter(item => item.value > 100)
    .map(item => transformItem(item));
  
  // For very large datasets, consider:
  // return Promise.all(
  //   data
  //     .filter(item => item.value > 100)
  //     .map(item => transformItemAsync(item))
  // );
}`}
              />
            </div>

            {/* Real-time Scoring Demo */}
            <div style={{
              background: 'rgba(78, 205, 196, 0.1)',
              padding: '30px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h3 style={{ margin: '0 0 20px 0', color: '#4ECDC4' }}>
                📊 Real-time Code Scoring Example
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '30px'
              }}>
                <div>
                  <h4 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>
                    Your Code
                  </h4>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    padding: '20px',
                    borderRadius: '8px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    position: 'relative'
                  }}>
                    <div style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#FF6B6B' }}>// Score: 5.2/10</span>
                    </div>
                    <div>function processData(arr) {'{'}</div>
                    <div>  let result = [];</div>
                    <div>  for(let i=0; i&lt;arr.length; i++) {'{'}</div>
                    <div>    if(arr[i] &gt; 10) {'{'}</div>
                    <div>      result.push(arr[i] * 2);</div>
                    <div>    {'}'}</div>
                    <div>  {'}'}</div>
                    <div>  return result;</div>
                    <div>{'}'}</div>
                    
                    <div style={{
                      position: 'absolute',
                      right: '10px',
                      top: '10px',
                      background: '#FF6B6B',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px'
                    }}>
                      Needs Optimization
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>
                    Artemis Suggestion
                  </h4>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    padding: '20px',
                    borderRadius: '8px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    position: 'relative'
                  }}>
                    <div style={{ marginBottom: '10px' }}>
                      <span style={{ color: '#4ECDC4' }}>// Score: 9.1/10</span>
                    </div>
                    <div>const processData = (arr) =&gt; {'{'}</div>
                    <div>  return arr</div>
                    <div>    .filter(item =&gt; item &gt; 10)</div>
                    <div>    .map(item =&gt; item * 2);</div>
                    <div>{'}'}</div>
                    
                    <div style={{
                      position: 'absolute',
                      right: '10px',
                      top: '10px',
                      background: '#4ECDC4',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px'
                    }}>
                      Optimized
                    </div>
                  </div>
                  
                  <div style={{
                    marginTop: '15px',
                    padding: '15px',
                    background: 'rgba(78, 205, 196, 0.2)',
                    borderRadius: '8px',
                    fontSize: '13px'
                  }}>
                    <strong>Improvements:</strong>
                    <ul style={{ margin: '5px 0 0 20px' }}>
                      <li>75% faster execution</li>
                      <li>More readable and maintainable</li>
                      <li>Functional programming approach</li>
                      <li>Memory efficient</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Code Review Phase */}
        {selectedPhase === 'review' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h2 style={{ margin: '0 0 30px 0', color: '#96CEB4' }}>
                🔍 AI-Powered Code Audit
              </h2>
              
              {/* Upload Interface */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(150, 206, 180, 0.1), rgba(150, 206, 180, 0.05))',
                padding: '40px',
                borderRadius: '12px',
                border: '2px dashed rgba(150, 206, 180, 0.4)',
                textAlign: 'center',
                marginBottom: '30px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>📤</div>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '20px', color: '#96CEB4' }}>
                  Upload Code for Comprehensive Audit
                </h3>
                <p style={{ margin: '0 0 20px 0', fontSize: '14px', opacity: 0.8 }}>
                  Drag & drop files or entire repositories for AI analysis
                </p>
                <button style={{
                  padding: '12px 30px',
                  background: '#96CEB4',
                  color: '#0F2027',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}>
                  Select Files
                </button>
              </div>

              {/* Audit Categories */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                {[
                  {
                    icon: '🏗️',
                    title: 'Architecture',
                    items: ['Design patterns', 'SOLID principles', 'Coupling & cohesion', 'Modularity']
                  },
                  {
                    icon: '⚡',
                    title: 'Performance',
                    items: ['Algorithm efficiency', 'Memory usage', 'Database queries', 'API calls']
                  },
                  {
                    icon: '🔒',
                    title: 'Security',
                    items: ['Vulnerability scan', 'Input validation', 'Authentication', 'Data protection']
                  },
                  {
                    icon: '🧹',
                    title: 'Code Quality',
                    items: ['Readability', 'Maintainability', 'Test coverage', 'Documentation']
                  }
                ].map((category, i) => (
                  <div key={i} style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(150, 206, 180, 0.3)'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>{category.icon}</div>
                    <h4 style={{ margin: '0 0 10px 0', color: '#96CEB4' }}>{category.title}</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', opacity: 0.9 }}>
                      {category.items.map((item, j) => (
                        <li key={j} style={{ marginBottom: '4px' }}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Sample Audit Report */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                padding: '25px',
                borderRadius: '12px'
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#96CEB4' }}>
                  📊 Sample Audit Report
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '15px',
                  marginBottom: '25px'
                }}>
                  <MetricCard label="Overall Score" value="7.8/10" trend={12} color="#96CEB4" />
                  <MetricCard label="Issues Found" value="23" trend={-8} color="#FFA726" />
                  <MetricCard label="Security Score" value="A-" color="#4ECDC4" />
                  <MetricCard label="Performance" value="85%" trend={5} color="#DDA0DD" />
                </div>

                <CodeExample
                  title="Detailed Findings Example"
                  color="#96CEB4"
                  code={`// Artemis Code Audit Report
// File: user-service.js
// Date: ${new Date().toLocaleDateString()}

CRITICAL ISSUES (3):
==================
1. SQL Injection Vulnerability (Line 45)
   - Raw SQL query with user input
   - Recommendation: Use parameterized queries
   
   // Current (Vulnerable):
   const query = \`SELECT * FROM users WHERE id = \${userId}\`;
   
   // Suggested Fix:
   const query = 'SELECT * FROM users WHERE id = ?';
   db.query(query, [userId]);

2. Memory Leak in Event Listeners (Line 120)
   - Event listeners not cleaned up
   - Impact: 2.3MB memory leak per hour
   
3. Unhandled Promise Rejection (Line 89)
   - Missing error handling in async operation

HIGH PRIORITY (8):
==================
- Inefficient database queries (N+1 problem)
- Missing input validation on 5 endpoints
- Hardcoded credentials in config
- No rate limiting on API endpoints

MEDIUM PRIORITY (12):
====================
- Code duplication in 3 modules (DRY violation)
- Missing unit tests for critical functions
- Inconsistent error handling patterns
- Outdated dependencies with known vulnerabilities

RECOMMENDATIONS:
================
1. Implement comprehensive input validation
2. Add rate limiting middleware
3. Refactor database queries to use ORM
4. Add automated security scanning to CI/CD
5. Increase test coverage to minimum 80%`}
                />
              </div>
            </div>
          </div>
        )}

        {/* CI/CD Phase */}
        {selectedPhase === 'cicd' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h2 style={{ margin: '0 0 30px 0', color: '#FFA726' }}>
                🚀 CI/CD Pipeline Integration
              </h2>
              
              {/* Integration Options */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '25px',
                marginBottom: '30px'
              }}>
                <FeatureCard
                  icon="🐙"
                  title="GitHub Actions"
                  description="Native GitHub integration for PR reviews"
                  features={[
                    'Automatic PR code review',
                    'Performance regression detection',
                    'Security vulnerability scanning',
                    'Code quality gates',
                    'Automated fix suggestions'
                  ]}
                  color="#FFA726"
                />
                
                <FeatureCard
                  icon="🦊"
                  title="GitLab CI"
                  description="Seamless GitLab pipeline integration"
                  features={[
                    'Merge request analysis',
                    'Pipeline quality gates',
                    'Incremental code scoring',
                    'Team performance metrics',
                    'Custom rule enforcement'
                  ]}
                  color="#FF6B6B"
                />
                
                <FeatureCard
                  icon="🔧"
                  title="Jenkins & Others"
                  description="Universal CI/CD platform support"
                  features={[
                    'REST API integration',
                    'Webhook notifications',
                    'Custom pipeline steps',
                    'Parallel analysis support',
                    'Result aggregation'
                  ]}
                  color="#4ECDC4"
                />
              </div>

              {/* PR Review Flow */}
              <div style={{
                background: 'rgba(255, 165, 38, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                marginBottom: '30px'
              }}>
                <h3 style={{ margin: '0 0 25px 0', color: '#FFA726' }}>
                  🔄 Automated PR Review Process
                </h3>
                
                <IntegrationFlow
                  title="How PR Reviews Work"
                  color="#FFA726"
                  steps={[
                    {
                      action: "Developer Opens PR",
                      description: "Code changes submitted for review"
                    },
                    {
                      action: "Artemis Webhook Triggered",
                      description: "CI/CD pipeline initiates Artemis analysis"
                    },
                    {
                      action: "Code Analysis",
                      description: "Comprehensive review of changes and impact"
                    },
                    {
                      action: "Generate Report",
                      description: "Detailed findings with suggestions"
                    },
                    {
                      action: "Post PR Comment",
                      description: "Results posted directly to PR with action items"
                    },
                    {
                      action: "Quality Gate",
                      description: "Pass/fail based on configured thresholds"
                    }
                  ]}
                />
              </div>

              {/* Configuration Examples */}
              <CodeExample
                title="GitHub Actions Configuration"
                color="#FFA726"
                code={`name: Artemis Code Review

on:
  pull_request:
    types: [opened, synchronize]

jobs:
  artemis-review:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
        with:
          fetch-depth: 0  # Full history for better analysis
      
      - name: Artemis Code Review
        uses: artemis-ai/code-review-action@v1
        with:
          api-key: \${{ secrets.ARTEMIS_API_KEY }}
          
          # Analysis configuration
          analysis:
            performance: true
            security: true
            code-quality: true
            architecture: true
          
          # Quality gates
          thresholds:
            min-score: 7.5
            max-critical-issues: 0
            max-high-issues: 3
            performance-regression: 10  # Max 10% regression
          
          # PR comment settings
          comment:
            enabled: true
            summary: true
            detailed-findings: true
            suggestions: true
            diff-annotations: true
      
      - name: Upload Artemis Report
        uses: actions/upload-artifact@v3
        if: always()
        with:
          name: artemis-report
          path: artemis-report.html`}
              />

              <CodeExample
                title="GitLab CI Configuration"
                color="#FF6B6B"
                code={`.artemis-review:
  stage: test
  image: artemis/cli:latest
  script:
    - artemis auth login --api-key $ARTEMIS_API_KEY
    
    # Analyze merge request changes
    - artemis analyze 
        --mode merge-request 
        --base-branch $CI_MERGE_REQUEST_TARGET_BRANCH_NAME
        --compare-branch $CI_MERGE_REQUEST_SOURCE_BRANCH_NAME
    
    # Check quality gates
    - artemis check-gates 
        --min-score 7.5 
        --max-issues critical=0,high=5
    
    # Generate and upload report
    - artemis report generate --format html --output artemis-report.html
    - artemis report post-mr-comment --detailed
    
  artifacts:
    reports:
      junit: artemis-junit.xml
    paths:
      - artemis-report.html
    expire_in: 30 days
    
  only:
    - merge_requests`}
              />

              {/* Sample PR Comment */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                padding: '25px',
                borderRadius: '12px',
                marginBottom: '30px'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#FFA726' }}>
                  💬 Sample PR Review Comment
                </h4>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}>
                  <div style={{ marginBottom: '15px' }}>
                    <strong>🤖 Artemis Code Review</strong> • Score: <span style={{ color: '#4ECDC4' }}>8.2/10</span> ✅
                  </div>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Summary:</strong> This PR improves the user authentication module with good performance optimizations. 
                    However, there are some security considerations that should be addressed.
                  </div>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <strong>🔍 Findings:</strong>
                    <div style={{ marginLeft: '20px', marginTop: '10px' }}>
                      🔴 <strong>Critical (1):</strong> Potential SQL injection in user lookup<br/>
                      🟡 <strong>High (2):</strong> Missing rate limiting, weak password validation<br/>
                      🟢 <strong>Improvements (3):</strong> 25% faster auth, reduced memory usage
                    </div>
                  </div>
                  
                  <div>
                    <strong>📋 Action Items:</strong>
                    <div style={{ marginLeft: '20px', marginTop: '10px' }}>
                      1. Fix SQL injection vulnerability in <code>auth.js:45</code><br/>
                      2. Add rate limiting to login endpoint<br/>
                      3. Consider using bcrypt rounds = 12 for better security
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Monitoring Phase */}
        {selectedPhase === 'monitoring' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h2 style={{ margin: '0 0 30px 0', color: '#DDA0DD' }}>
                📊 Continuous Code Quality Monitoring
              </h2>
              
              {/* Dashboard Preview */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(221, 160, 221, 0.1), rgba(147, 112, 219, 0.1))',
                padding: '30px',
                borderRadius: '12px',
                marginBottom: '30px'
              }}>
                <h3 style={{ margin: '0 0 25px 0', color: '#DDA0DD' }}>
                  Real-time Quality Dashboard
                </h3>
                
                {/* Metrics Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '20px',
                  marginBottom: '30px'
                }}>
                  <MetricCard label="Code Quality Score" value="8.4" trend={5} color="#DDA0DD" />
                  <MetricCard label="Technical Debt" value="124h" trend={-12} color="#4ECDC4" />
                  <MetricCard label="Coverage" value="87%" trend={3} color="#96CEB4" />
                  <MetricCard label="Complexity" value="Low" color="#FFA726" />
                </div>

                {/* Trend Chart Placeholder */}
                <div style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  padding: '20px',
                  borderRadius: '8px',
                  height: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📈</div>
                    <div style={{ fontSize: '16px', opacity: 0.8 }}>
                      Code Quality Trend Over Time
                    </div>
                  </div>
                </div>
              </div>

              {/* Monitoring Features */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '25px'
              }}>
                <FeatureCard
                  icon="📈"
                  title="Trend Analysis"
                  description="Track code quality evolution over time"
                  features={[
                    'Historical quality metrics',
                    'Performance regression alerts',
                    'Technical debt tracking',
                    'Team productivity insights',
                    'Predictive quality forecasting'
                  ]}
                  color="#DDA0DD"
                />
                
                <FeatureCard
                  icon="🎯"
                  title="Smart Alerts"
                  description="Proactive notifications for code issues"
                  features={[
                    'Quality threshold breaches',
                    'Security vulnerability detection',
                    'Performance degradation',
                    'Complexity increase warnings',
                    'Best practice violations'
                  ]}
                  color="#FF6B6B"
                />
                
                <FeatureCard
                  icon="📊"
                  title="Team Analytics"
                  description="Insights for continuous improvement"
                  features={[
                    'Developer performance metrics',
                    'Code review efficiency',
                    'Knowledge sharing patterns',
                    'Hotspot identification',
                    'Sprint quality reports'
                  ]}
                  color="#4ECDC4"
                />
              </div>
            </div>
          </div>
        )}

        {/* Integration Architecture */}
        <div style={{
          marginTop: '40px',
          background: 'rgba(255, 255, 255, 0.05)',
          padding: '40px',
          borderRadius: '16px'
        }}>
          <h2 style={{ margin: '0 0 30px 0', fontSize: '28px' }}>
            🏗️ Complete Integration Architecture
          </h2>
          
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '30px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <svg viewBox="0 0 800 400" style={{ maxWidth: '100%', height: 'auto' }}>
              {/* Developer */}
              <rect x="50" y="150" width="120" height="100" rx="8" fill="rgba(78, 205, 196, 0.2)" stroke="#4ECDC4" strokeWidth="2"/>
              <text x="110" y="190" textAnchor="middle" fill="white" fontSize="14" fontWeight="600">Developer</text>
              <text x="110" y="210" textAnchor="middle" fill="white" fontSize="24">💻</text>
              
              {/* IDE */}
              <rect x="250" y="50" width="120" height="80" rx="8" fill="rgba(150, 206, 180, 0.2)" stroke="#96CEB4" strokeWidth="2"/>
              <text x="310" y="85" textAnchor="middle" fill="white" fontSize="14">VS Code</text>
              <text x="310" y="105" textAnchor="middle" fill="white" fontSize="20">🔧</text>
              
              {/* Git */}
              <rect x="250" y="270" width="120" height="80" rx="8" fill="rgba(255, 165, 38, 0.2)" stroke="#FFA726" strokeWidth="2"/>
              <text x="310" y="305" textAnchor="middle" fill="white" fontSize="14">Git/GitHub</text>
              <text x="310" y="325" textAnchor="middle" fill="white" fontSize="20">🐙</text>
              
              {/* Artemis Cloud */}
              <rect x="450" y="125" width="150" height="150" rx="8" fill="rgba(221, 160, 221, 0.2)" stroke="#DDA0DD" strokeWidth="2"/>
              <text x="525" y="155" textAnchor="middle" fill="white" fontSize="16" fontWeight="600">Artemis AI</text>
              <image href="https://artemis.turintech.ai/images/artemis-letter.svg" x="485" y="170" width="80" height="80" />
              <text x="525" y="265" textAnchor="middle" fill="white" fontSize="12">Cloud/On-Prem</text>
              
              {/* CI/CD */}
              <rect x="680" y="50" width="120" height="80" rx="8" fill="rgba(255, 107, 107, 0.2)" stroke="#FF6B6B" strokeWidth="2"/>
              <text x="740" y="85" textAnchor="middle" fill="white" fontSize="14">CI/CD</text>
              <text x="740" y="105" textAnchor="middle" fill="white" fontSize="20">🚀</text>
              
              {/* Monitoring */}
              <rect x="680" y="270" width="120" height="80" rx="8" fill="rgba(78, 205, 196, 0.2)" stroke="#4ECDC4" strokeWidth="2"/>
              <text x="740" y="305" textAnchor="middle" fill="white" fontSize="14">Monitoring</text>
              <text x="740" y="325" textAnchor="middle" fill="white" fontSize="20">📊</text>
              
              {/* Arrows */}
              <path d="M 170 200 L 250 90" stroke="#96CEB4" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
              <path d="M 170 200 L 250 310" stroke="#FFA726" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
              <path d="M 370 90 L 450 165" stroke="#DDA0DD" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
              <path d="M 370 310 L 450 235" stroke="#DDA0DD" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
              <path d="M 600 165 L 680 90" stroke="#FF6B6B" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
              <path d="M 600 235 L 680 310" stroke="#4ECDC4" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)"/>
              
              {/* Arrow marker */}
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="white" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>

        {/* Benefits Summary */}
        <div style={{
          marginTop: '40px',
          background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(221, 160, 221, 0.1))',
          padding: '40px',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <h2 style={{ margin: '0 0 30px 0', fontSize: '28px' }}>
            🎯 Why Artemis Throughout SDLC?
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {[
              { icon: '⚡', title: '60% Faster Development', desc: 'AI-powered suggestions and automation' },
              { icon: '🛡️', title: '90% Fewer Bugs', desc: 'Catch issues before they reach production' },
              { icon: '📈', title: '45% Performance Gain', desc: 'Continuous optimization at every stage' },
              { icon: '💰', title: '70% Cost Reduction', desc: 'Less time debugging, more time building' }
            ].map((benefit, i) => (
              <div key={i} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '30px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>{benefit.icon}</div>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>{benefit.title}</h3>
                <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
          </>
        )}

        {/* User Stories View */}
        {selectedView === 'stories' && (
          <div>
            {/* User Personas Header */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(221, 160, 221, 0.1), rgba(147, 112, 219, 0.1))',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '40px',
              textAlign: 'center'
            }}>
              <h2 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '36px',
                background: 'linear-gradient(135deg, #DDA0DD, #9370DB)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                🎭 Real-World User Stories
              </h2>
              <p style={{ 
                fontSize: '18px', 
                opacity: 0.9,
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                See how different personas use Artemis to transform their development workflow
              </p>
            </div>

            {/* Developer Stories */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h3 style={{ 
                margin: '0 0 30px 0', 
                fontSize: '28px',
                color: '#4ECDC4',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                <span style={{ fontSize: '36px' }}>👨‍💻</span>
                Individual Developer Stories
              </h3>

                             {/* Story 1: Real-time Performance Scoring */}
               <div style={{
                 background: 'rgba(78, 205, 196, 0.1)',
                 padding: '30px',
                 borderRadius: '12px',
                 marginBottom: '25px',
                 border: '1px solid rgba(78, 205, 196, 0.3)'
               }}>
                 <h4 style={{ margin: '0 0 15px 0', color: '#4ECDC4', fontSize: '20px' }}>
                   "I want to write performant code from the start"
                 </h4>
                 <div style={{
                   display: 'grid',
                   gridTemplateColumns: '1fr 2fr',
                   gap: '30px',
                   alignItems: 'center'
                 }}>
                   <div style={{
                     background: 'rgba(0, 0, 0, 0.3)',
                     padding: '20px',
                     borderRadius: '8px',
                     textAlign: 'center'
                   }}>
                     <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚡</div>
                     <div style={{ fontSize: '16px', fontWeight: '600' }}>Performance Score</div>
                     <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                       Live in IDE
                     </div>
                   </div>
                   <div>
                     <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                       <strong>Scenario:</strong> Writing a data processing function. Artemis shows 
                       performance score dropping from 8.2 to 3.1 as I type.
                     </p>
                     <div style={{
                       background: 'rgba(0, 0, 0, 0.2)',
                       padding: '15px',
                       borderRadius: '8px',
                       fontSize: '14px'
                     }}>
                       <strong>Real-time performance feedback:</strong>
                       <ul style={{ margin: '10px 0 0 20px' }}>
                         <li><strong>Performance alert:</strong> "O(n²) complexity detected"</li>
                         <li><strong>Cost impact:</strong> "Will cost $450/month at scale"</li>
                         <li><strong>Artemis suggests:</strong> "Use HashMap for O(1) lookups"</li>
                         <li><strong>One-click fix:</strong> Transforms to optimal algorithm</li>
                         <li><strong>New metrics:</strong> O(n), $12/month, 50x faster</li>
                       </ul>
                     </div>
                     <div style={{
                       marginTop: '15px',
                       padding: '10px 15px',
                       background: 'rgba(78, 205, 196, 0.2)',
                       borderRadius: '8px',
                       fontSize: '14px',
                       fontWeight: '600'
                     }}>
                       💡 Result: Prevent performance issues before they reach production
                     </div>
                   </div>
                 </div>
               </div>

                             {/* Story 2: Algorithmic Intelligence */}
               <div style={{
                 background: 'rgba(150, 206, 180, 0.1)',
                 padding: '30px',
                 borderRadius: '12px',
                 marginBottom: '25px',
                 border: '1px solid rgba(150, 206, 180, 0.3)'
               }}>
                 <h4 style={{ margin: '0 0 15px 0', color: '#96CEB4', fontSize: '20px' }}>
                   "This algorithm is slow but I don't know why"
                 </h4>
                 <div style={{
                   display: 'grid',
                   gridTemplateColumns: '1fr 2fr',
                   gap: '30px',
                   alignItems: 'center'
                 }}>
                   <div style={{
                     background: 'rgba(0, 0, 0, 0.3)',
                     padding: '20px',
                     borderRadius: '8px',
                     textAlign: 'center'
                   }}>
                     <div style={{ fontSize: '48px', marginBottom: '10px' }}>🧮</div>
                     <div style={{ fontSize: '16px', fontWeight: '600' }}>Algorithm AI</div>
                     <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                       Deep Analysis
                     </div>
                   </div>
                   <div>
                     <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                       <strong>Scenario:</strong> My data processing takes 30s for 10K items. 
                       Profiler shows nested loops but I need the business logic.
                     </p>
                     <div style={{
                       background: 'rgba(0, 0, 0, 0.2)',
                       padding: '15px',
                       borderRadius: '8px',
                       fontSize: '14px'
                     }}>
                       <strong>Artemis algorithmic analysis:</strong>
                       <ul style={{ margin: '10px 0 0 20px' }}>
                         <li><strong>Identifies issue:</strong> "O(n³) due to redundant lookups"</li>
                         <li><strong>Understands intent:</strong> "You're finding unique combinations"</li>
                         <li><strong>Suggests algorithm:</strong> "Use hash maps + sliding window"</li>
                         <li><strong>Generates solution:</strong> Complete refactor with same output</li>
                         <li><strong>Proves correctness:</strong> "Here's why it's equivalent"</li>
                       </ul>
                     </div>
                     <div style={{
                       marginTop: '15px',
                       padding: '10px 15px',
                       background: 'rgba(150, 206, 180, 0.2)',
                       borderRadius: '8px',
                       fontSize: '14px',
                       fontWeight: '600'
                     }}>
                       💡 Result: 30s → 0.3s (100x faster), O(n) complexity, same results
                     </div>
                   </div>
                 </div>
               </div>

                             {/* Story 3: Performance-First Code Generation */}
               <div style={{
                 background: 'rgba(221, 160, 221, 0.1)',
                 padding: '30px',
                 borderRadius: '12px',
                 marginBottom: '25px',
                 border: '1px solid rgba(221, 160, 221, 0.3)'
               }}>
                 <h4 style={{ margin: '0 0 15px 0', color: '#DDA0DD', fontSize: '20px' }}>
                   "I need to implement this feature with optimal performance"
                 </h4>
                 <div style={{
                   display: 'grid',
                   gridTemplateColumns: '1fr 2fr',
                   gap: '30px',
                   alignItems: 'center'
                 }}>
                   <div style={{
                     background: 'rgba(0, 0, 0, 0.3)',
                     padding: '20px',
                     borderRadius: '8px',
                     textAlign: 'center'
                   }}>
                     <div style={{ fontSize: '48px', marginBottom: '10px' }}>🚀</div>
                     <div style={{ fontSize: '16px', fontWeight: '600' }}>Fast Generation</div>
                     <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                       Performance First
                     </div>
                   </div>
                   <div>
                     <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                       <strong>Scenario:</strong> Need to implement real-time search across 1M products. 
                       Performance is critical.
                     </p>
                     <div style={{
                       background: 'rgba(0, 0, 0, 0.2)',
                       padding: '15px',
                       borderRadius: '8px',
                       fontSize: '14px'
                     }}>
                       <strong>Artemis performance-optimized generation:</strong>
                       <ul style={{ margin: '10px 0 0 20px' }}>
                         <li><strong>Request:</strong> "Generate search with &lt;10ms response"</li>
                         <li><strong>Artemis analyzes:</strong> Data size, query patterns, infrastructure</li>
                         <li><strong>Generates solution:</strong></li>
                         <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                           <li>Trie-based search index</li>
                           <li>Memory-mapped caching</li>
                           <li>Parallel query execution</li>
                           <li>Connection pooling</li>
                         </ul>
                         <li><strong>Performance guarantee:</strong> "7ms p99 latency at 1000 QPS"</li>
                       </ul>
                     </div>
                     <div style={{
                       marginTop: '15px',
                       padding: '10px 15px',
                       background: 'rgba(221, 160, 221, 0.2)',
                       borderRadius: '8px',
                       fontSize: '14px',
                       fontWeight: '600'
                     }}>
                       💡 Result: Production-ready search that's 10x faster than typical implementations
                     </div>
                   </div>
                 </div>
               </div>

                               {/* Story 4: Smart SonarQube Fixes */}
                <div style={{
                  background: 'rgba(255, 165, 38, 0.1)',
                  padding: '30px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 165, 38, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#FFA726', fontSize: '20px' }}>
                    "SonarQube found issues but I don't understand how to fix them properly"
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 2fr',
                    gap: '30px',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>🧠</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>AI Fixes</div>
                      <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                        Beyond Rules
                      </div>
                    </div>
                    <div>
                      <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                        <strong>Scenario:</strong> SonarQube shows "Cognitive Complexity: 32" on my validation 
                        function. Its suggestion to "extract methods" would create 10 tiny functions.
                      </p>
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}>
                        <strong>Artemis AI understanding in IDE:</strong>
                        <ul style={{ margin: '10px 0 0 20px' }}>
                          <li><strong>Right-click issue:</strong> "Fix with Artemis AI"</li>
                          <li><strong>AI analyzes:</strong> "This is validation logic with business rules"</li>
                          <li><strong>Smart solution:</strong> "Convert to declarative validation schema"</li>
                          <li><strong>Shows refactor:</strong></li>
                          <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                            <li>Rule-based validation object</li>
                            <li>Reusable validators</li>
                            <li>Clear error messages</li>
                            <li>70% less code</li>
                          </ul>
                          <li><strong>Bonus:</strong> "Also fixed the security issue SonarQube missed"</li>
                       </ul>
                      </div>
                      <div style={{
                        marginTop: '15px',
                        padding: '10px 15px',
                        background: 'rgba(255, 165, 38, 0.2)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        💡 Result: Complexity: 5, more maintainable, actually better code
                      </div>
                    </div>
                  </div>
                </div>
            </div>

                         {/* Data Scientist Stories */}
             <div style={{
               background: 'rgba(255, 255, 255, 0.05)',
               padding: '40px',
               borderRadius: '16px',
               marginBottom: '30px'
             }}>
               <h3 style={{ 
                 margin: '0 0 30px 0', 
                 fontSize: '28px',
                 color: '#9370DB',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '15px'
               }}>
                 <span style={{ fontSize: '36px' }}>📊</span>
                 Data Scientist Stories
               </h3>

               {/* Story: Quick Analysis App */}
               <div style={{
                 background: 'rgba(147, 112, 219, 0.1)',
                 padding: '30px',
                 borderRadius: '12px',
                 marginBottom: '25px',
                 border: '1px solid rgba(147, 112, 219, 0.3)'
               }}>
                 <h4 style={{ margin: '0 0 15px 0', color: '#9370DB', fontSize: '20px' }}>
                   "My manager needs a quick analysis dashboard by tomorrow"
                 </h4>
                 <div style={{
                   display: 'grid',
                   gridTemplateColumns: '1fr 2fr',
                   gap: '30px',
                   alignItems: 'center'
                 }}>
                   <div style={{
                     background: 'rgba(0, 0, 0, 0.3)',
                     padding: '20px',
                     borderRadius: '8px',
                     textAlign: 'center'
                   }}>
                     <div style={{ fontSize: '48px', marginBottom: '10px' }}>📈</div>
                     <div style={{ fontSize: '16px', fontWeight: '600' }}>Template Evolution</div>
                     <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                       Artemis Intelligence
                     </div>
                   </div>
                   <div>
                     <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                       <strong>Scenario:</strong> I have customer churn data and need an interactive 
                       analysis app with ML insights for tomorrow's board meeting.
                     </p>
                     <div style={{
                       background: 'rgba(0, 0, 0, 0.2)',
                       padding: '15px',
                       borderRadius: '8px',
                       fontSize: '14px'
                     }}>
                       <strong>What happens:</strong>
                       <ul style={{ margin: '10px 0 0 20px' }}>
                         <li>Upload CSV data to Artemis</li>
                         <li>Select "Customer Analytics" template</li>
                         <li>Artemis evolves template to match my data</li>
                         <li>Generates interactive Streamlit app with:</li>
                         <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                           <li>Auto-generated visualizations</li>
                           <li>ML models (churn prediction)</li>
                           <li>Interactive AI insights chat</li>
                           <li>Statistical summaries</li>
                         </ul>
                         <li>Deploy with one click</li>
                       </ul>
                     </div>
                     <div style={{
                       marginTop: '15px',
                       padding: '10px 15px',
                       background: 'rgba(147, 112, 219, 0.2)',
                       borderRadius: '8px',
                       fontSize: '14px',
                       fontWeight: '600'
                     }}>
                       💡 Result: 2-day analysis work done in 1 hour, interactive app impresses board
                     </div>
                   </div>
                 </div>
               </div>

                               {/* Story: ML Pipeline Performance */}
                <div style={{
                  background: 'rgba(78, 205, 196, 0.1)',
                  padding: '30px',
                  borderRadius: '12px',
                  marginBottom: '25px',
                  border: '1px solid rgba(78, 205, 196, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#4ECDC4', fontSize: '20px' }}>
                    "My ML training takes 8 hours and costs $500 per run"
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 2fr',
                    gap: '30px',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚡</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>ML Performance</div>
                      <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                        Fast & Cheap
                      </div>
                    </div>
                    <div>
                      <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                        <strong>Scenario:</strong> Deep learning pipeline for fraud detection. 
                        8-hour training is killing our iteration speed.
                      </p>
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}>
                        <strong>Artemis ML performance optimization:</strong>
                        <ul style={{ margin: '10px 0 0 20px' }}>
                          <li><strong>Analyzes pipeline:</strong> "Redundant preprocessing every epoch"</li>
                          <li><strong>Finds inefficiencies:</strong> "Using CPU for tensor ops"</li>
                          <li><strong>Optimizes code:</strong></li>
                          <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                            <li>Caches preprocessed data</li>
                            <li>Vectorizes operations</li>
                            <li>Mixed precision training</li>
                            <li>Gradient accumulation</li>
                          </ul>
                          <li><strong>New performance:</strong> 45 minutes, $35/run</li>
                          <li><strong>Same accuracy:</strong> 94.2% (unchanged)</li>
                        </ul>
                      </div>
                      <div style={{
                        marginTop: '15px',
                        padding: '10px 15px',
                        background: 'rgba(78, 205, 196, 0.2)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        💡 Result: 10x faster training, 93% cost reduction, same model quality
                      </div>
                    </div>
                  </div>
                </div>

                {/* Story: Feature Engineering Evolution */}
                <div style={{
                  background: 'rgba(255, 165, 38, 0.1)',
                  padding: '30px',
                  borderRadius: '12px',
                  marginBottom: '25px',
                  border: '1px solid rgba(255, 165, 38, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#FFA726', fontSize: '20px' }}>
                    "I'm stuck on feature engineering for my model"
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 2fr',
                    gap: '30px',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>🧬</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>Feature Evolution</div>
                      <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                        LLM + Genetic Search
                      </div>
                    </div>
                    <div>
                      <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                        <strong>Scenario:</strong> I have customer data but can't get my churn model 
                        above 75% accuracy. I suspect I'm missing important features.
                      </p>
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}>
                        <strong>What happens:</strong>
                        <ul style={{ margin: '10px 0 0 20px' }}>
                          <li>Upload current feature engineering code</li>
                          <li>Artemis analyzes data patterns & domain</li>
                          <li>LLM suggests creative feature ideas:</li>
                          <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                            <li>Time-based aggregations</li>
                            <li>Interaction features</li>
                            <li>Domain-specific transformations</li>
                            <li>External data enrichment</li>
                          </ul>
                          <li>Genetic algorithm evolves & combines features</li>
                          <li>Each variation scored on:</li>
                          <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                            <li>Predictive power</li>
                            <li>Computational cost</li>
                            <li>Business interpretability</li>
                          </ul>
                        </ul>
                      </div>
                      <div style={{
                        marginTop: '15px',
                        padding: '10px 15px',
                        background: 'rgba(255, 165, 38, 0.2)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        💡 Result: 87% accuracy with 15 new engineered features
                      </div>
                    </div>
                  </div>
                </div>

                {/* Story: Production Optimization */}
                <div style={{
                  background: 'rgba(221, 160, 221, 0.1)',
                  padding: '30px',
                  borderRadius: '12px',
                  border: '1px solid rgba(221, 160, 221, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#DDA0DD', fontSize: '20px' }}>
                    "My model works great but fails in production"
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 2fr',
                    gap: '30px',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚡</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>Production Ready</div>
                      <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                        End-to-End
                      </div>
                    </div>
                    <div>
                      <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                        <strong>Scenario:</strong> My deep learning model has 95% accuracy but uses 4GB RAM, 
                        takes 2s per prediction, and crashes under load.
                      </p>
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}>
                        <strong>What happens:</strong>
                        <ul style={{ margin: '10px 0 0 20px' }}>
                          <li>Artemis analyzes production constraints</li>
                          <li>Optimizes across multiple metrics:</li>
                          <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                            <li><strong>Memory:</strong> Model quantization, pruning</li>
                            <li><strong>Latency:</strong> ONNX conversion, batching</li>
                            <li><strong>Throughput:</strong> Async processing, caching</li>
                            <li><strong>Reliability:</strong> Error handling, fallbacks</li>
                          </ul>
                          <li>Generates production-ready code with:</li>
                          <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                            <li>API endpoint implementation</li>
                            <li>Load balancing logic</li>
                            <li>Monitoring & logging</li>
                            <li>Graceful degradation</li>
                          </ul>
                        </ul>
                      </div>
                      <div style={{
                        marginTop: '15px',
                        padding: '10px 15px',
                        background: 'rgba(221, 160, 221, 0.2)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        💡 Result: 500MB RAM, 100ms latency, 1000 QPS, 94% accuracy maintained
                      </div>
                    </div>
                  </div>
                </div>
              </div>

             {/* Engineering Lead Stories */}
             <div style={{
               background: 'rgba(255, 255, 255, 0.05)',
               padding: '40px',
               borderRadius: '16px',
               marginBottom: '30px'
             }}>
               <h3 style={{ 
                 margin: '0 0 30px 0', 
                 fontSize: '28px',
                 color: '#DDA0DD',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '15px'
               }}>
                 <span style={{ fontSize: '36px' }}>👨‍💼</span>
                 Engineering Lead Stories
               </h3>

               {/* Story 4: Custom Code Audit */}
               <div style={{
                 background: 'rgba(221, 160, 221, 0.1)',
                 padding: '30px',
                 borderRadius: '12px',
                 marginBottom: '25px',
                 border: '1px solid rgba(221, 160, 221, 0.3)'
               }}>
                 <h4 style={{ margin: '0 0 15px 0', color: '#DDA0DD', fontSize: '20px' }}>
                   "I need to understand our codebase from a specific angle"
                 </h4>
                 <div style={{
                   display: 'grid',
                   gridTemplateColumns: '1fr 2fr',
                   gap: '30px',
                   alignItems: 'center'
                 }}>
                   <div style={{
                     background: 'rgba(0, 0, 0, 0.3)',
                     padding: '20px',
                     borderRadius: '8px',
                     textAlign: 'center'
                   }}>
                     <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔍</div>
                     <div style={{ fontSize: '16px', fontWeight: '600' }}>Flexible Audit</div>
                     <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                       Ask Anything
                     </div>
                   </div>
                   <div>
                     <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                       <strong>Scenario:</strong> New CTO wants to know: "Which parts of our code 
                       would be hardest for new hires to understand?"
                     </p>
                     <div style={{
                       background: 'rgba(0, 0, 0, 0.2)',
                       padding: '15px',
                       borderRadius: '8px',
                       fontSize: '14px'
                     }}>
                       <strong>What I do with Artemis:</strong>
                       <ul style={{ margin: '10px 0 0 20px' }}>
                         <li>Upload entire codebase (5000 files)</li>
                         <li>Ask: "Score each file on junior developer readability"</li>
                         <li>Get instant heat map of complexity</li>
                         <li>Drill down: "Why is payment service hard to understand?"</li>
                         <li>Follow up: "Which files need documentation most?"</li>
                         <li>Export findings as spreadsheet for team planning</li>
                       </ul>
                     </div>
                     <div style={{
                       marginTop: '15px',
                       padding: '10px 15px',
                       background: 'rgba(221, 160, 221, 0.2)',
                       borderRadius: '8px',
                       fontSize: '14px',
                       fontWeight: '600'
                     }}>
                       💡 Result: Targeted onboarding plan + documentation priorities
                     </div>
                   </div>
                 </div>
               </div>

               {/* Story 5: Team Pattern Analysis */}
               <div style={{
                 background: 'rgba(255, 107, 107, 0.1)',
                 padding: '30px',
                 borderRadius: '12px',
                 marginBottom: '25px',
                 border: '1px solid rgba(255, 107, 107, 0.3)'
               }}>
                 <h4 style={{ margin: '0 0 15px 0', color: '#FF6B6B', fontSize: '20px' }}>
                   "Which teams are following our new coding standards?"
                 </h4>
                 <div style={{
                   display: 'grid',
                   gridTemplateColumns: '1fr 2fr',
                   gap: '30px',
                   alignItems: 'center'
                 }}>
                   <div style={{
                     background: 'rgba(0, 0, 0, 0.3)',
                     padding: '20px',
                     borderRadius: '8px',
                     textAlign: 'center'
                   }}>
                     <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
                     <div style={{ fontSize: '16px', fontWeight: '600' }}>Pattern Analysis</div>
                     <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                       Team Insights
                     </div>
                   </div>
                   <div>
                     <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                       <strong>Scenario:</strong> We adopted new API design standards 3 months ago. 
                       Are teams actually following them?
                     </p>
                     <div style={{
                       background: 'rgba(0, 0, 0, 0.2)',
                       padding: '15px',
                       borderRadius: '8px',
                       fontSize: '14px'
                     }}>
                       <strong>Using Artemis for team analysis:</strong>
                       <ul style={{ margin: '10px 0 0 20px' }}>
                         <li>Upload all team repos grouped by team</li>
                         <li>Query: "Score each team's APIs on our REST standards"</li>
                         <li>Get breakdown: Team A: 85%, Team B: 45%, Team C: 92%</li>
                         <li>Deep dive: "Show me Team B's non-compliant endpoints"</li>
                         <li>Follow up: "Which patterns are they using instead?"</li>
                         <li>Export report for team retrospectives</li>
                       </ul>
                     </div>
                     <div style={{
                       marginTop: '15px',
                       padding: '10px 15px',
                       background: 'rgba(255, 107, 107, 0.2)',
                       borderRadius: '8px',
                       fontSize: '14px',
                       fontWeight: '600'
                     }}>
                       💡 Result: Targeted training for Team B, best practices from Team C
                     </div>
                   </div>
                 </div>
               </div>

                                                                                                                               {/* Story 6: Cloud Cost Analysis */}
                <div style={{
                  background: 'rgba(150, 206, 180, 0.1)',
                  padding: '30px',
                  borderRadius: '12px',
                  border: '1px solid rgba(150, 206, 180, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#96CEB4', fontSize: '20px' }}>
                    "Our cloud bill is $200K/month - where's it all going?"
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 2fr',
                    gap: '30px',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>💰</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>Cost Analysis</div>
                      <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                        Performance → $$$
                      </div>
                    </div>
                    <div>
                      <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                        <strong>Scenario:</strong> CFO wants 30% cloud cost reduction without impacting 
                        performance. Where do we start?
                      </p>
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}>
                        <strong>Artemis performance-to-cost analysis:</strong>
                        <ul style={{ margin: '10px 0 0 20px' }}>
                          <li>Upload all service codebases</li>
                          <li>Query: "Which services are inefficient for their load?"</li>
                          <li>Analysis reveals: Data service using O(n²) algorithm</li>
                          <li>Cost impact: "$45K/month on unnecessary compute"</li>
                          <li>Follow up: "Show me all database N+1 queries"</li>
                          <li>Artemis finds: 23 services with fixable inefficiencies</li>
                          <li>Total savings potential: $68K/month (34%)</li>
                        </ul>
                      </div>
                      <div style={{
                        marginTop: '15px',
                        padding: '10px 15px',
                        background: 'rgba(150, 206, 180, 0.2)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        💡 Result: Clear roadmap to cut costs through performance optimization
                      </div>
                    </div>
                  </div>
                </div>
             </div>

            {/* CI/CD & Automation Stories */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '40px',
              borderRadius: '16px',
              marginBottom: '30px'
            }}>
              <h3 style={{ 
                margin: '0 0 30px 0', 
                fontSize: '28px',
                color: '#FFA726',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                <span style={{ fontSize: '36px' }}>🤖</span>
                Automation & CI/CD Stories
              </h3>

              {/* Story 6: PR Automation */}
              <div style={{
                background: 'rgba(255, 165, 38, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                marginBottom: '25px',
                border: '1px solid rgba(255, 165, 38, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#FFA726', fontSize: '20px' }}>
                  "Every PR should be automatically reviewed"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔄</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>CI/CD Integration</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      GitHub Actions
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> We want consistent code quality across all PRs 
                      without manual review overhead.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>What happens:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Developer opens PR</li>
                        <li>Artemis automatically analyzes changes</li>
                        <li>Posts detailed comment with score & issues</li>
                        <li>Blocks merge if quality gate fails</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(255, 165, 38, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: 90% fewer bugs reach production
                    </div>
                  </div>
                </div>
              </div>

              {/* Story 7: Task Delegation */}
              <div style={{
                background: 'rgba(78, 205, 196, 0.1)',
                padding: '30px',
                borderRadius: '12px',
                border: '1px solid rgba(78, 205, 196, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#4ECDC4', fontSize: '20px' }}>
                  "I need a complex feature built by AI"
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '30px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎨</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Coding Agent</div>
                    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '5px' }}>
                      Task delegation
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '15px' }}>
                      <strong>Scenario:</strong> I need to add caching to our API but don't have 
                      time to implement it myself.
                    </p>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      <strong>What happens:</strong>
                      <ul style={{ margin: '10px 0 0 20px' }}>
                        <li>Open Artemis web interface</li>
                        <li>Describe task: "Add Redis caching to user API"</li>
                        <li>Artemis coding agent implements solution</li>
                        <li>Review generated code and tests</li>
                        <li>Create PR with one click</li>
                      </ul>
                    </div>
                    <div style={{
                      marginTop: '15px',
                      padding: '10px 15px',
                      background: 'rgba(78, 205, 196, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      💡 Result: 2-day task completed in 30 minutes
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Value Summary Diagram */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(221, 160, 221, 0.1))',
              padding: '40px',
              borderRadius: '16px',
              marginTop: '40px'
            }}>
              <h3 style={{ 
                margin: '0 0 30px 0', 
                fontSize: '28px',
                textAlign: 'center'
              }}>
                🎯 Artemis Value Across All Touchpoints
              </h3>
              
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                padding: '30px',
                borderRadius: '12px'
              }}>
                <svg viewBox="0 0 900 500" style={{ maxWidth: '100%', height: 'auto' }}>
                                     {/* Central Artemis Hub */}
                   <circle cx="450" cy="250" r="80" fill="rgba(221, 160, 221, 0.3)" stroke="#DDA0DD" strokeWidth="3"/>
                   <text x="450" y="220" textAnchor="middle" fill="white" fontSize="18" fontWeight="600">Artemis AI</text>
                   <image href="https://artemis.turintech.ai/images/artemis-letter.svg" x="420" y="235" width="60" height="60" />
                  
                                     {/* User Personas */}
                   {[
                     { x: 150, y: 100, label: 'Developer', icon: '💻', color: '#4ECDC4' },
                     { x: 750, y: 100, label: 'Lead', icon: '👨‍💼', color: '#DDA0DD' },
                     { x: 150, y: 400, label: 'CI/CD', icon: '🚀', color: '#FFA726' },
                     { x: 750, y: 400, label: 'Data Scientist', icon: '📊', color: '#9370DB' },
                     { x: 450, y: 400, label: 'Web User', icon: '🌐', color: '#96CEB4' }
                   ].map((persona, i) => (
                    <g key={i}>
                      <circle cx={persona.x} cy={persona.y} r="60" fill={`${persona.color}20`} stroke={persona.color} strokeWidth="2"/>
                      <text x={persona.x} y={persona.y - 10} textAnchor="middle" fill="white" fontSize="24">{persona.icon}</text>
                      <text x={persona.x} y={persona.y + 20} textAnchor="middle" fill="white" fontSize="14">{persona.label}</text>
                      <line x1={persona.x} y1={persona.y} x2="450" y2="250" stroke={persona.color} strokeWidth="2" opacity="0.5"/>
                    </g>
                  ))}
                  
                  {/* Value Props */}
                  <text x="450" y="50" textAnchor="middle" fill="#4ECDC4" fontSize="16" fontWeight="600">Real-time Feedback</text>
                  <text x="100" y="250" textAnchor="middle" fill="#FFA726" fontSize="16" fontWeight="600">Automation</text>
                  <text x="800" y="250" textAnchor="middle" fill="#96CEB4" fontSize="16" fontWeight="600">Team Insights</text>
                  <text x="450" y="450" textAnchor="middle" fill="#DDA0DD" fontSize="16" fontWeight="600">Quality Gates</text>
                </svg>
              </div>
              
              <div style={{
                marginTop: '30px',
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                One Platform • Multiple Touchpoints • Continuous Value
              </div>
            </div>

            {/* Why Artemis Section */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(221, 160, 221, 0.1))',
              padding: '40px',
              borderRadius: '16px',
              marginTop: '40px',
              border: '1px solid rgba(78, 205, 196, 0.3)'
            }}>
              <h3 style={{ 
                margin: '0 0 30px 0', 
                fontSize: '28px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #4ECDC4, #DDA0DD)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                🤔 Why Artemis When You Have Other Tools?
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '25px',
                marginBottom: '30px'
              }}>
                                 {/* Traditional Tools Limitations */}
                 <div style={{
                   background: 'rgba(255, 107, 107, 0.1)',
                   padding: '25px',
                   borderRadius: '12px',
                   border: '1px solid rgba(255, 107, 107, 0.3)'
                 }}>
                   <h4 style={{ margin: '0 0 15px 0', color: '#FF6B6B' }}>
                     ❌ What Traditional Tools Can't Do at Scale
                   </h4>
                   <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
                     <li><strong>Performance scoring:</strong> "Rate algorithmic efficiency across 10K files"</li>
                     <li><strong>Cost analysis:</strong> "Which code patterns waste cloud resources?"</li>
                     <li><strong>Custom metrics:</strong> "Score all APIs on latency potential"</li>
                     <li><strong>Intent understanding:</strong> "Is this trying to be fast? How to improve?"</li>
                     <li><strong>Scale analysis:</strong> "Will this code handle 10x load?"</li>
                   </ul>
                 </div>

                                 {/* Artemis AI Capabilities */}
                 <div style={{
                   background: 'rgba(78, 205, 196, 0.1)',
                   padding: '25px',
                   borderRadius: '12px',
                   border: '1px solid rgba(78, 205, 196, 0.3)'
                 }}>
                   <h4 style={{ margin: '0 0 15px 0', color: '#4ECDC4' }}>
                     ✅ Artemis Performance Intelligence at Scale
                   </h4>
                   <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
                     <li><strong>Performance scoring:</strong> "Rate all services on algorithmic efficiency"</li>
                     <li><strong>Cost prediction:</strong> "Estimate cloud costs for each service"</li>
                     <li><strong>Bottleneck detection:</strong> "Find O(n²) or worse across codebase"</li>
                     <li><strong>Optimization suggestions:</strong> "Generate faster alternatives"</li>
                     <li><strong>Impact analysis:</strong> "If we 10x traffic, what breaks first?"</li>
                   </ul>
                 </div>

                                 {/* Perfect Together */}
                 <div style={{
                   background: 'rgba(221, 160, 221, 0.1)',
                   padding: '25px',
                   borderRadius: '12px',
                   border: '1px solid rgba(221, 160, 221, 0.3)'
                 }}>
                   <h4 style={{ margin: '0 0 15px 0', color: '#DDA0DD' }}>
                     🚀 Real Performance Wins at Scale
                   </h4>
                   <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: '1.8' }}>
                     <li><strong>10K files analyzed:</strong> Found $2M/year in wasteful algorithms</li>
                     <li><strong>API optimization:</strong> Reduced p99 latency by 85% across services</li>
                     <li><strong>ML pipelines:</strong> Cut training costs by 90% (same accuracy)</li>
                     <li><strong>Database queries:</strong> Eliminated N+1 patterns saving 70% CPU</li>
                     <li><strong>Cloud migration:</strong> Predicted & fixed scale issues pre-launch</li>
                   </ul>
                 </div>
              </div>

              {/* Key Differentiator */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                padding: '30px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <h4 style={{ 
                  margin: '0 0 20px 0', 
                  fontSize: '24px',
                  color: '#4ECDC4'
                }}>
                  The Artemis Difference
                </h4>
                <p style={{ 
                  fontSize: '18px', 
                  lineHeight: '1.6',
                  maxWidth: '800px',
                  margin: '0 auto'
                }}>
                  <strong>Traditional tools find problems. Artemis understands and fixes them.</strong>
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  lineHeight: '1.6',
                  opacity: 0.9,
                  marginTop: '15px',
                  maxWidth: '800px',
                  margin: '15px auto 0'
                }}>
                  While SonarQube says "reduce complexity", Artemis says "here's a state machine pattern 
                  that's 50% faster and actually maintainable". While Snyk says "vulnerability found", 
                  Artemis says "fixed it, updated tests, and prevented similar issues".
                </p>
              </div>

              {/* Integration Message */}
              <div style={{
                marginTop: '30px',
                padding: '20px',
                background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.2), rgba(221, 160, 221, 0.2))',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <p style={{ 
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  🚀 Keep your existing tools. Add Artemis to make them actionable.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtemisSDLC; 